import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SEO } from "@/components/SEO";
import { Mail, MessageSquare, Github, Globe } from "lucide-react";

export default function Contact() {
    return (
        <div className="min-h-screen bg-gradient-subtle flex flex-col">
            <SEO
                title="Contact - TranslateLoo AI"
                description="Contact TranslateLoo AI for support, feedback, or inquiries"
            />
            <Header />

            <main className="flex-1 max-w-4xl mx-auto px-4 py-12 sm:py-16">
                <article className="space-y-12">
                    {/* Header */}
                    <header className="text-center space-y-4">
                        <div className="inline-flex p-3 bg-gradient-primary rounded-2xl mb-4">
                            <MessageSquare className="h-10 w-10 text-white" />
                        </div>
                        <h1 className="text-4xl sm:text-5xl font-bold bg-gradient-primary bg-clip-text text-transparent">
                            Contact Us
                        </h1>
                        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                            Have questions, feedback, or need support? We'd love to hear from you.
                        </p>
                    </header>

                    {/* Contact Options */}
                    <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <ContactCard
                            icon={<Mail className="h-6 w-6" />}
                            title="Email Support"
                            description="For general inquiries and support requests"
                            action="contact@translateloo.com"
                            href="mailto:contact@translateloo.com"
                        />
                        <ContactCard
                            icon={<MessageSquare className="h-6 w-6" />}
                            title="Feedback"
                            description="Share your thoughts and suggestions"
                            action="Use in-app feedback"
                            href="/"
                        />
                        <ContactCard
                            icon={<Github className="h-6 w-6" />}
                            title="GitHub"
                            description="Report issues or contribute to the project"
                            action="View Repository"
                            href="https://github.com/translateloo"
                        />
                        <ContactCard
                            icon={<Globe className="h-6 w-6" />}
                            title="Website"
                            description="Visit our main translator application"
                            action="translateloo.com"
                            href="/"
                        />
                    </section>

                    {/* FAQ Section */}
                    <section className="space-y-6">
                        <h2 className="text-2xl font-semibold text-foreground text-center">
                            Frequently Asked Questions
                        </h2>
                        <div className="space-y-4">
                            <FaqItem
                                question="Is TranslateLoo AI free to use?"
                                answer="Yes! TranslateLoo AI is free for personal use. We offer a generous number of daily translations without requiring an account."
                            />
                            <FaqItem
                                question="What languages are supported?"
                                answer="We support a wide range of languages including English, Spanish, French, German, Portuguese, Italian, Japanese, Chinese, Korean, and many more. Our AI can also auto-detect the source language."
                            />
                            <FaqItem
                                question="Is my data secure?"
                                answer="Yes. We take privacy seriously. Translations are processed securely and we do not store your translation data on our servers long-term. See our Privacy Policy for more details."
                            />
                            <FaqItem
                                question="How accurate are the translations?"
                                answer="Our AI-powered translations are highly accurate for most content. However, for critical or legal documents, we recommend professional human review."
                            />
                        </div>
                    </section>

                    {/* Response Time */}
                    <section className="bg-translator-panel/50 rounded-2xl p-8 border border-translator-border text-center">
                        <h2 className="text-xl font-semibold text-foreground mb-2">Response Time</h2>
                        <p className="text-muted-foreground">
                            We typically respond to emails within 24-48 hours during business days.
                            For urgent matters, please include "URGENT" in your subject line.
                        </p>
                    </section>
                </article>
            </main>

            <Footer />
        </div>
    );
}

function ContactCard({
    icon,
    title,
    description,
    action,
    href,
}: {
    icon: React.ReactNode;
    title: string;
    description: string;
    action: string;
    href: string;
}) {
    const isExternal = href.startsWith("http") || href.startsWith("mailto:");

    return (
        <a
            href={href}
            target={isExternal ? "_blank" : undefined}
            rel={isExternal ? "noopener noreferrer" : undefined}
            className="block p-6 rounded-xl bg-translator-panel/50 border border-translator-border hover:border-primary/30 hover:shadow-lg transition-all group"
        >
            <div className="inline-flex p-2 bg-primary/10 text-primary rounded-lg mb-3 group-hover:bg-primary group-hover:text-white transition-colors">
                {icon}
            </div>
            <h3 className="font-semibold text-foreground mb-1">{title}</h3>
            <p className="text-sm text-muted-foreground mb-3">{description}</p>
            <span className="text-sm font-medium text-primary">{action} →</span>
        </a>
    );
}

function FaqItem({ question, answer }: { question: string; answer: string }) {
    return (
        <div className="p-6 rounded-xl bg-translator-panel/50 border border-translator-border">
            <h3 className="font-semibold text-foreground mb-2">{question}</h3>
            <p className="text-sm text-muted-foreground">{answer}</p>
        </div>
    );
}
